import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API, fmtMoney, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Download, Trash2, FileText, Lock, ArrowLeft, Link as LinkIcon, ShieldCheck, Clock } from "lucide-react";
import { PaymentRails } from "@/components/PaymentRails";

const DOC_KINDS = [
  // NY-focused HR bundle — primary personnel-file kinds admins manage here.
  { v: "application",     l: "Job Application" },
  { v: "education",       l: "Education Records" },
  { v: "certification",   l: "Certifications" },
  { v: "attestation",     l: "Attestation" },
  // Legacy onboarding paperwork
  { v: "i9",              l: "I-9 · Employment Eligibility" },
  { v: "w4",              l: "W-4 · Federal Withholding" },
  { v: "direct_deposit",  l: "Direct Deposit Authorization" },
  { v: "id",              l: "Photo ID" },
  { v: "handbook",        l: "Signed Handbook / Policies" },
  { v: "other",           l: "Other" },
];

// Quick-upload buttons at the top of the upload panel — the three that admins
// touch daily. Everything else lives in the dropdown fallback below.
const QUICK_KINDS = [
  { v: "application",   l: "Job Application" },
  { v: "education",     l: "Education Records" },
  { v: "certification", l: "Certification" },
];

/** Days since a YYYY-MM-DD ISO date, floored. Empty → null. */
function daysSince(iso) {
  if (!iso) return null;
  const then = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(+then)) return null;
  return Math.floor((Date.now() - +then) / 86_400_000);
}

export default function EmployeeDetail() {
  const { id } = useParams();
  const [emp, setEmp] = useState(null);
  const [tab, setTab] = useState("overview");
  const [docs, setDocs] = useState([]);
  const [pro, setPro] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [kind, setKind] = useState("i9");
  const [requireSig, setRequireSig] = useState(false);
  const fileRef = useRef();

  const loadEmp = useCallback(async () => {
    const { data } = await api.get(`/employees/${id}`);
    setEmp(data);
  }, [id]);
  useEffect(() => { loadEmp(); }, [loadEmp]);

  const loadDocs = useCallback(async () => {
    try {
      const [d, b] = await Promise.all([
        api.get(`/employees/${id}/documents`),
        api.get("/billing/status").catch(() => ({ data: { pro: { active: false } } })),
      ]);
      setDocs(d.data || []);
      setPro(b.data?.pro || { active: false });
    } catch (e) { toast.error(formatApiError(e)); }
  }, [id]);
  useEffect(() => { if (tab === "documents") loadDocs(); }, [tab, loadDocs]);

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("File must be under 5MB"); e.target.value = ""; return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/employees/${id}/documents`, {
          filename: f.name, kind,
          data_url: String(reader.result),
          signature_required: requireSig,
        });
        toast.success(requireSig ? "Uploaded — employee will be asked to e-sign" : "Uploaded");
        loadDocs();
      } catch (err) {
        const detail = formatApiError(err);
        toast.error(detail.includes("Professional") ? "Professional plan required — see Billing" : detail);
      } finally {
        setUploading(false);
        if (fileRef.current) fileRef.current.value = "";
      }
    };
    reader.readAsDataURL(f);
  };

  const removeDoc = async (docId, name) => {
    if (!window.confirm(`Delete ${name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/employees/${id}/documents/${docId}`);
      toast.success("Deleted");
      loadDocs();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!emp) return <div className="p-10 font-mono">Loading…</div>;

  return (
    <div className="p-10">
      <Link to="/app/employees" className="overline inline-flex items-center gap-1 mb-3"><ArrowLeft className="w-3 h-3" /> Employees</Link>
      <h1 className="font-heading font-black text-5xl tracking-tight" data-testid="emp-name">{emp.first_name} {emp.last_name}</h1>
      <div className="font-mono text-xs text-muted-foreground mt-1">ID · {emp.id} · {emp.email}</div>

      <div className="flex border-b-2 border-ink mt-8 mb-6">
        <TabBtn active={tab === "overview"} onClick={() => setTab("overview")} testid="tab-overview">Overview</TabBtn>
        <TabBtn active={tab === "documents"} onClick={() => setTab("documents")} testid="tab-documents">Documents</TabBtn>
      </div>

      {tab === "overview" && (
        <>
          <div className="hard-card p-6 font-mono text-sm space-y-2 max-w-2xl" data-testid="overview-panel">
          <div><b>Email:</b> {emp.email}</div>
          <div><b>State:</b> {emp.state}</div>
          <div><b>Type:</b> {emp.employment_type}</div>
          <div><b>Rate:</b> {emp.employment_type === "hourly" ? `${fmtMoney(emp.hourly_rate)}/hr` : `${fmtMoney(emp.annual_salary)}/yr`}</div>
          <div><b>Filing:</b> {emp.filing_status}</div>
          <div><b>Status:</b> {emp.status}</div>
          <div><b>Sick / Vacation Balance:</b> {emp.sick_balance_hours || 0}h · {emp.vacation_balance_hours || 0}h</div>
          </div>
          <PaymentRails employee={emp} onChanged={loadEmp} />
        </>
      )}

      {tab === "documents" && (
        <div data-testid="documents-panel">
          <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="overline mb-1">§ PERSONNEL FILE</div>
              <h2 className="font-heading font-black text-2xl tracking-tight">Applications · Certifications · Attestations</h2>
              <div className="font-mono text-xs text-muted-foreground mt-1 max-w-2xl">
                Admin-only workspace for {emp.first_name}'s HR documents. Upload, download, and share — the employee can view these in their portal only after clearing their 90-day probation.
              </div>
            </div>
            {pro && !pro.active && (
              <div data-testid="pro-lock-banner" className="border-2 border-warn bg-warn/15 p-3 max-w-md">
                <div className="font-mono text-xs flex items-center gap-2"><Lock className="w-3 h-3" /> <b>Professional feature.</b> <Link to="/app/billing" className="underline">Upgrade in Billing</Link> to upload new documents.</div>
              </div>
            )}
          </div>

          <ProbationBanner emp={emp} />
          {(() => {
            const d = daysSince(emp.hire_date);
            const cleared = d != null && d >= 90;
            return (
              <div className="mb-4 text-[11px] font-mono text-muted-foreground" data-testid="employee-visibility-note">
                {cleared
                  ? "✓ Employee can view these documents (read-only) in their portal — admin retains full control."
                  : "🔒 Employee cannot see any of these documents in their portal yet — visibility unlocks after 90 days from hire date."}
              </div>
            );
          })()}

          <div className="hard-card p-4 mb-6" data-testid="upload-card">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <label className="overline">§ UPLOAD</label>
              <span className="font-mono text-[10px] text-muted-foreground">Pick a category · PDF, JPG, PNG, DOC up to 5MB</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              {QUICK_KINDS.map((k) => (
                <button
                  key={k.v}
                  data-testid={`quick-upload-${k.v}`}
                  onClick={() => { setKind(k.v); fileRef.current?.click(); }}
                  disabled={uploading || (pro && !pro.active)}
                  className="hard-btn py-3 px-4 font-mono uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Upload className="w-3.5 h-3.5" /> {k.l}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-2 mb-3 font-mono text-xs cursor-pointer" data-testid="require-signature-row">
              <input
                data-testid="require-signature-checkbox"
                type="checkbox"
                checked={requireSig}
                onChange={(e) => setRequireSig(e.target.checked)}
                className="w-4 h-4 accent-ink"
              />
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>Require employee e-signature (Handbook · Attestation · Policies)</span>
            </label>
            <div className="flex items-center gap-3 flex-wrap pt-3 border-t border-ink/20">
              <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">Other category</span>
              <select data-testid="doc-kind" value={kind} onChange={(e) => setKind(e.target.value)} className="border-2 border-ink px-3 py-2 font-mono text-sm">
                {DOC_KINDS.map((k) => <option key={k.v} value={k.v}>{k.l}</option>)}
              </select>
              <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={handleUpload} data-testid="doc-file-input" className="hidden" />
              <button
                data-testid="doc-upload-btn"
                onClick={() => fileRef.current?.click()}
                disabled={uploading || (pro && !pro.active)}
                className="border-2 border-ink px-4 py-2 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary"
              ><Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Choose File"}</button>
            </div>
          </div>

          <div className="hard-card overflow-x-auto">
            <table className="w-full font-mono text-sm" data-testid="docs-table">
              <thead className="bg-ink text-white">
                <tr>
                  <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Preview</th>
                  <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Kind</th>
                  <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Filename</th>
                  <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Signature</th>
                  <th className="text-right px-3 py-3 uppercase text-xs tracking-widest">Size</th>
                  <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Uploaded By</th>
                  <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">When</th>
                  <th className="w-32"></th>
                </tr>
              </thead>
              <tbody>
                {docs.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">No documents yet.</td></tr>}
                {docs.map((d, i) => (
                  <tr key={d.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F9F9F9]"} data-testid={`doc-row-${d.id}`}>
                    <td className="px-3 py-3 border-t border-ink/20">
                      {d.thumbnail_data_url ? (
                        <a href={`${API}/employees/${id}/documents/${d.id}/download`} target="_blank" rel="noreferrer" data-testid={`doc-thumb-${d.id}`}>
                          <img src={d.thumbnail_data_url} alt="preview" className="w-14 h-16 object-cover border border-ink" />
                        </a>
                      ) : (
                        <div className="w-14 h-16 border border-ink/40 bg-secondary/40 flex items-center justify-center"><FileText className="w-5 h-5 text-ink/50" /></div>
                      )}
                    </td>
                    <td className="px-3 py-3 border-t border-ink/20"><span className="uppercase text-[10px] font-black px-2 py-0.5 border border-ink">{d.kind.replace("_", " ")}</span></td>
                    <td className="px-3 py-3 border-t border-ink/20 flex items-center gap-2"><FileText className="w-3.5 h-3.5" /> {d.filename}</td>
                    <td className="px-3 py-3 border-t border-ink/20 text-xs whitespace-nowrap">
                      {d.signature_required ? (
                        d.signed_at ? (
                          <span data-testid={`doc-signed-${d.id}`} className="inline-flex items-center gap-1 text-good"><ShieldCheck className="w-3.5 h-3.5" /> Signed {new Date(d.signed_at).toLocaleDateString()}</span>
                        ) : (
                          <span data-testid={`doc-sig-pending-${d.id}`} className="inline-flex items-center gap-1 text-warn"><Clock className="w-3.5 h-3.5" /> Pending signature</span>
                        )
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 border-t border-ink/20 text-right tabular-nums">{d.size_kb} KB</td>
                    <td className="px-3 py-3 border-t border-ink/20 text-xs">{d.uploaded_by}</td>
                    <td className="px-3 py-3 border-t border-ink/20 text-xs text-muted-foreground">{new Date(d.uploaded_at).toLocaleString()}</td>
                    <td className="px-3 py-3 border-t border-ink/20 text-right space-x-1 whitespace-nowrap">
                      <button
                        data-testid={`doc-share-${d.id}`}
                        onClick={() => {
                          const url = `${API}/employees/${id}/documents/${d.id}/download`;
                          navigator.clipboard?.writeText(url);
                          toast.success("Admin download link copied");
                        }}
                        className="border-2 border-ink p-1 inline-flex hover:bg-ink hover:text-white"
                        title="Copy shareable admin download link"
                      ><LinkIcon className="w-3.5 h-3.5" /></button>
                      <a data-testid={`doc-download-${d.id}`} href={`${API}/employees/${id}/documents/${d.id}/download`} target="_blank" rel="noreferrer" className="border-2 border-ink p-1 inline-flex hover:bg-ink hover:text-white" title="Download"><Download className="w-3.5 h-3.5" /></a>
                      <button data-testid={`doc-delete-${d.id}`} onClick={() => removeDoc(d.id, d.filename)} disabled={pro && !pro.active} className="border-2 border-bad text-bad p-1 hover:bg-bad hover:text-white disabled:opacity-40" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-5 py-3 font-mono uppercase text-xs tracking-widest ${active ? "bg-ink text-white" : "hover:bg-secondary"}`}
    >{children}</button>
  );
}

function ProbationBanner({ emp }) {
  const d = daysSince(emp.hire_date);
  if (d == null) {
    return (
      <div className="border-2 border-warn bg-warn/10 p-3 mb-5 font-mono text-xs" data-testid="probation-missing-hiredate">
        <b>Hire date missing</b> — set it on the employee's overview so the 90-day probation clock can start.
      </div>
    );
  }
  if (d >= 90) {
    return (
      <div className="border-2 border-good bg-good/10 p-3 mb-5 font-mono text-xs" data-testid="probation-cleared-banner">
        <b>Probation cleared</b> · {d} days since hire ({emp.hire_date}). {emp.first_name} can now view these documents (read-only) in the portal. You keep full upload / delete / share control.
      </div>
    );
  }
  return (
    <div className="border-2 border-ink bg-secondary/60 p-3 mb-5 font-mono text-xs" data-testid="probation-locked-banner">
      <b>Probation active</b> · {d}/90 days since hire ({emp.hire_date}) — <b>{90 - d}</b> day{90 - d === 1 ? "" : "s"} until {emp.first_name} can see these documents in their portal. You have full access now.
    </div>
  );
}
